import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import ViewToggle from './ViewToggle';

interface TopBarProps {
  onOpenSidebar: () => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  viewMode: 'grid' | 'list';
  onViewChange: (mode: 'grid' | 'list') => void;
}

export default function TopBar({
  onOpenSidebar,
  searchQuery,
  onSearchChange,
  viewMode,
  onViewChange,
}: TopBarProps) {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div
      className={`sticky top-0 z-40 px-3 md:px-6 py-2.5 md:py-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-3 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm border-b border-background-200/70'
          : 'bg-background-50 border-b border-background-200/70'
      }`}
    >
      {/* Top row: hamburger + title + right actions */}
      <div className="flex items-center justify-between gap-2 sm:gap-4">
        <div className="flex items-center gap-2 md:gap-3 min-w-0">
          {/* Hamburger - mobile/tablet only */}
          <button
            onClick={onOpenSidebar}
            className="lg:hidden btn-press w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg text-foreground-600 hover:bg-background-100 active:bg-background-200 transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-menu-line text-lg md:text-xl" />
          </button>
          <h1 className="font-heading text-sm md:text-lg lg:text-xl font-bold text-foreground-950 truncate">
            {t('hero.title')}
          </h1>
        </div>

        {/* Right actions - visible on mobile top row */}
        <div className="flex items-center gap-1 sm:hidden flex-shrink-0">
          <ViewToggle viewMode={viewMode} onChange={onViewChange} />
          <LanguageSwitcher variant="light" />
        </div>
      </div>

      {/* Search box - full width on mobile, flex-1 on desktop */}
      <div className="flex-1 max-w-xl relative w-full">
        <span className="absolute start-3 md:start-4 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 flex items-center justify-center text-foreground-400 pointer-events-none">
          <i className="ri-search-line text-sm md:text-base" />
        </span>
        <input
          type="text"
          placeholder={t('menu.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full pl-9 md:pl-12 pr-8 md:pr-10 py-2 md:py-2.5 bg-background-100 border border-background-200 rounded-xl text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all"
        />
        {searchQuery && (
          <button
            onClick={() => onSearchChange('')}
            className="btn-press absolute end-2.5 md:end-3 top-1/2 -translate-y-1/2 w-5 h-5 md:w-6 md:h-6 flex items-center justify-center text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-sm md:text-base" />
          </button>
        )}
      </div>

      {/* Right actions - hidden on mobile, shown on sm+ */}
      <div className="hidden sm:flex items-center gap-1 flex-shrink-0">
        <ViewToggle viewMode={viewMode} onChange={onViewChange} />
        <LanguageSwitcher variant="light" />
      </div>
    </div>
  );
}